import { useState, useEffect, useCallback, useRef } from 'react';
import Session, { useSessionContext } from 'supertokens-auth-react/recipe/session';
import {
  getAllAppsWithAdminEmails,
  getAllAppLicenses,
  getAllAdminsWithApps,
  getTotalUserCount,
  getAllApps,
  getAllAppSubscriptions,
  getAllUsers,
  getAllUserPermissions,
  getProductTotals,
  type ProductTotals,
} from '@/Api';
import { isApiError } from '@/services/httpClient';
import { IApp, IAppLicense, IAppSubscription, FeatureFlag } from '@/types/App.model';
import { IUser } from '@/types/User';
import { IUserPermissions } from '@/types/UserPermission';

/**
 * MasterAdminData: Data structure for MasterAdmin dashboard
 * Contains all platform management data for ROBOTOP employees
 */
interface MasterAdminData {
  apps: any[];
  licenses: IAppLicense[];
  admins: any[];
  totalUsers: number;
  portfolioApps: IApp[];
  portfolioLicenses: IAppLicense[];
  subscriptions: IAppSubscription[];
  users: IUser[];
  permissions: IUserPermissions[];
  /** null until loaded, or when the server has no totals endpoint yet. */
  productTotals: ProductTotals | null;
  isLoading: boolean;
  isAuthorized: boolean | null;
  error: string | null;
}

interface UseMasterAdminDataReturn extends MasterAdminData {
  refreshData: () => Promise<void>;
  refreshApps: () => Promise<void>;
  refreshLicenses: () => Promise<void>;
  refreshAdmins: () => Promise<void>;
  refreshUsers: () => Promise<void>;
}

/**
 * Hook for MasterAdmin data management
 * Provides access to all platform data for ROBOTOP employees
 * @returns MasterAdmin data and refresh functions
 */
export const useMasterAdminData = (): UseMasterAdminDataReturn => {
  const session = useSessionContext();
  const sessionUserId = !session.loading && session.doesSessionExist ? session.userId : '';
  const requestIdRef = useRef(0);
  const [data, setData] = useState<MasterAdminData>({
    apps: [],
    licenses: [],
    admins: [],
    totalUsers: 0,
    portfolioApps: [],
    portfolioLicenses: [],
    subscriptions: [],
    users: [],
    permissions: [],
    productTotals: null,
    isLoading: true,
    isAuthorized: null,
    error: null,
  });

  const fetchInitialData = useCallback(async () => {
    if (!sessionUserId) return;
    const requestId = ++requestIdRef.current;
    const stillCurrent = () => requestId === requestIdRef.current;

    const loadAuthorization = () =>
      Promise.all([
        getAllAppsWithAdminEmails(),
        getAllAppLicenses(),
        getAllAdminsWithApps(),
        getTotalUserCount(),
      ]);

    try {
      setData(prev => ({ ...prev, isLoading: true, error: null, isAuthorized: null }));

      let appsData;
      let licensesData;
      let adminsData;
      let totalUsersData;
      try {
        [appsData, licensesData, adminsData, totalUsersData] = await loadAuthorization();
      } catch (firstError) {
        const canRefresh =
          isApiError(firstError) &&
          firstError.response.status === 401 &&
          (await Session.doesSessionExist());
        if (!canRefresh || !(await Session.attemptRefreshingSession())) {
          throw firstError;
        }
        [appsData, licensesData, adminsData, totalUsersData] = await loadAuthorization();
      }

      if (!stillCurrent()) return;
      setData(prev => ({
        ...prev,
        apps: appsData,
        licenses: licensesData,
        admins: adminsData,
        totalUsers: totalUsersData,
        isAuthorized: true,
        isLoading: false,
      }));
    } catch (error) {
      if (!stillCurrent()) return;
      console.log('User not authorized for Master Admin');
      setData(prev => ({
        ...prev,
        isAuthorized: false,
        isLoading: false,
        error: 'Not authorized',
      }));
    }
  }, [sessionUserId]);

  const fetchAdditionalData = useCallback(async () => {
    // Separate from the batch below so an older server without the endpoint only blanks these cards.
    getProductTotals()
      .then((productTotals) => setData((prev) => ({ ...prev, productTotals })))
      .catch((error) => console.error('Error fetching product totals:', error));

    try {
      const [users, permissions, portfolioApps, portfolioLicenses, subscriptions] = await Promise.all([
        getAllUsers(),
        getAllUserPermissions(),
        getAllApps(),
        getAllAppLicenses(),
        getAllAppSubscriptions(),
      ]);

      setData(prev => ({
        ...prev,
        users,
        permissions,
        portfolioApps,
        portfolioLicenses,
        subscriptions,
      }));
    } catch (error) {
      console.error('Error fetching additional data:', error);
      setData(prev => ({
        ...prev,
        error: 'Failed to fetch additional data',
      }));
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchInitialData(), fetchAdditionalData()]);
  }, [fetchInitialData, fetchAdditionalData]);

  const refreshApps = useCallback(async () => {
    try {
      const [appsData, portfolioAppsData] = await Promise.all([
        getAllAppsWithAdminEmails(),
        getAllApps(),
      ]);
      setData(prev => ({
        ...prev,
        apps: appsData,
        portfolioApps: portfolioAppsData,
      }));
    } catch (error) {
      console.error('Error refreshing apps:', error);
    }
  }, []);

  const refreshLicenses = useCallback(async () => {
    try {
      const [licensesData, portfolioLicensesData] = await Promise.all([
        getAllAppLicenses(),
        getAllAppLicenses(),
      ]);
      setData(prev => ({
        ...prev,
        licenses: licensesData,
        portfolioLicenses: portfolioLicensesData,
      }));
    } catch (error) {
      console.error('Error refreshing licenses:', error);
    }
  }, []);

  const refreshAdmins = useCallback(async () => {
    try {
      const adminsData = await getAllAdminsWithApps();
      setData(prev => ({
        ...prev,
        admins: adminsData,
      }));
    } catch (error) {
      console.error('Error refreshing admins:', error);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      const [usersData, permissionsData] = await Promise.all([
        getAllUsers(),
        getAllUserPermissions(),
      ]);
      setData(prev => ({
        ...prev,
        users: usersData,
        permissions: permissionsData,
      }));
    } catch (error) {
      console.error('Error refreshing users:', error);
    }
  }, []);

  useEffect(() => {
    if (session.loading) return;
    if (!sessionUserId) {
      requestIdRef.current += 1;
      setData(prev => ({
        ...prev,
        isAuthorized: false,
        isLoading: false,
      }));
      return;
    }
    void fetchInitialData();
  }, [session.loading, sessionUserId, fetchInitialData]);

  useEffect(() => {
    if (data.isAuthorized) {
      fetchAdditionalData();
    }
  }, [data.isAuthorized, fetchAdditionalData]);

  return {
    ...data,
    refreshData,
    refreshApps,
    refreshLicenses,
    refreshAdmins,
    refreshUsers,
  };
}; 